/**
 * The maintainer surface: two report queues, one report, and one session's reports.
 * ADR-0026, ADR-0031.
 *
 * Every route answers anyone who is not a maintainer with a plain 404, the same as a route that
 * does not exist. So `getMaintainer` reads a 404 as "not a maintainer" rather than as a failure,
 * and the rail shows the Maintainer section only when it resolves to an email.
 */

import { authed } from "./authed";
import { ApiError } from "./errors";
import type {
  CategoryChange,
  Report,
  ReportAbout,
  ReportKind,
  ReportPage,
  ReportState,
  SessionReports,
  StateChange,
} from "./types";

/** Reports on one page of a queue. */
export const PAGE_SIZE = 25;

export interface QueueFilter {
  kind: ReportKind;
  state: ReportState | "all";
  about?: ReportAbout;
  /** Text in the report, a skill, template or tool name, a page, a session id or a flow run. */
  q?: string;
}

/** The maintainer's email, or `null` when this account is not one. */
export async function getMaintainer(signal?: AbortSignal): Promise<string | null> {
  try {
    const me = await authed<{ email: string }>("/maintainer/me", { signal });
    return me.email;
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return null;
    throw cause;
  }
}

export function listReports(
  filter: QueueFilter,
  offset: number,
  signal?: AbortSignal,
): Promise<ReportPage> {
  const query = new URLSearchParams({
    kind: filter.kind,
    state: filter.state,
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (filter.about !== undefined) query.set("about", filter.about);
  if (filter.q !== undefined && filter.q.trim() !== "") query.set("q", filter.q.trim());
  return authed<ReportPage>(`/maintainer/reports?${query.toString()}`, { signal });
}

export function getReport(id: string, signal?: AbortSignal): Promise<Report> {
  return authed<Report>(`/maintainer/reports/${id}`, { signal });
}

export function moveReport(id: string, change: StateChange): Promise<Report> {
  return authed<Report>(`/maintainer/reports/${id}`, { method: "PATCH", body: change });
}

export function recategorise(id: string, change: CategoryChange): Promise<Report> {
  return authed<Report>(`/maintainer/reports/${id}/category`, { method: "PUT", body: change });
}

export function getSession(sessionId: string, signal?: AbortSignal): Promise<SessionReports> {
  return authed<SessionReports>(`/maintainer/sessions/${encodeURIComponent(sessionId)}`, {
    signal,
  });
}
